/**
 * Preference labels — single source of truth for the human-readable
 * field names shown on per-type forms AND on the read-time
 * RunPreferencesPanel.
 *
 * Workflow Prompt 11 retired the mechanical camelCase humanizer
 * (which produced "Max Substation Distance Mi Amber" for
 * `maxSubstationDistanceMiAmber`). Labels are now editorial — the
 * user sees the same string in the wizard and in the saved
 * report's preferences panel.
 *
 * The corresponding test file (labels.test.ts) iterates the type
 * interfaces from src/types/siteRag.ts and asserts every key has
 * an entry here, so a new preference field can't ship without a
 * label entry.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import type { BopHeatRejection, CoolingGeneration } from '../../types/siteRag';

export const PREFERENCE_LABELS: Record<ReportTypeId, Record<string, string>> = {
  feasibility: {
    // SSOT — projectSizeMW / customerCategory / inServiceDate /
    // riskTolerance / powerSourcePreference / coolingPreference DELETED.
    // Live canonically on SiteEntity.projectIntent — their labels live
    // alongside the Project Intent editor surface, not here.
    kpiWeightOverrides: 'KPI weight overrides',
  },
  environmental: {
    expectedNepaPathway: 'Expected NEPA pathway',
    federalNexus: 'Federal nexus',
    speciesConcerns: 'Species concerns',
    tribalLandProximityKnown: 'Tribal-land proximity known',
    airBasinOverride: 'Air-basin override',
    dischargeWaterTarget: 'Discharge water target',
  },
  'test-fit': {
    targetFootprintAcres: 'Target footprint (acres)',
    phasing: 'Phasing',
    phaseCount: 'Phase count',
    setbackOverrideFt: 'Setback override (ft)',
    parkingRatioPerEmployee: 'Parking ratio (per employee)',
    stormwaterDetentionGal: 'Stormwater detention (gal)',
  },
  'power-availability': {
    // SSOT — powerRequirementMW DELETED (lives on projectIntent).
    timeToPowerCriticalDate: 'Time-to-power critical date',
    renewablesPreference: 'Renewables preference',
    btmStrategy: 'BTM strategy',
    maxSubstationDistanceMiAmber: 'Substation distance — amber threshold (mi)',
    maxSubstationDistanceMiRed: 'Substation distance — red threshold (mi)',
    curtailmentRiskTolerance: 'Curtailment risk tolerance',
  },
  'water-resource': {
    // SSOT — coolingType DELETED (lives on projectIntent.coolingStrategy).
    waterRightsKnown: 'Water rights known',
    droughtScenario: 'Drought scenario',
    wastewaterTarget: 'Wastewater target',
  },
  interconnection: {
    latencyTargetsMs: 'Latency targets (ms)',
    carrierDiversityMin: 'Minimum carrier diversity',
    bandwidthGbps: 'Bandwidth (Gbps)',
    ixPeeringRequired: 'IX peering required',
    submarineCableProximityImportant: 'Submarine-cable proximity important',
  },
  'supply-chain-risk': {
    kpiWeightOverrides: 'KPI weight overrides',
  },
  // PSR-1 (row 0.99290) — Public Sentiment Report preference labels.
  'public-sentiment': {
    trendWindowDays: 'Trend window (days)',
    benchmarkCohortSize: 'Comparable-site cohort size',
    includeTribalRecords: 'Include tribal-government records',
  },
  'custom-brief': {
    briefName: 'Brief name',
    objective: 'Objective',
    questions: 'Questions',
    contextNotes: 'Context notes',
    counterparties: 'Counterparties',
    keyDates: 'Key dates',
  },
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report preference labels.
  financial: {
    hurdleRateOverride: 'Hurdle / target IRR',
    pinnedPathwayComparisonId: 'Pinned pathway comparison',
    includeTaxCredits: 'Include IRA tax credits (ITC/PTC)',
    includePpaAdjustment: 'Include PPA revenue adjustment',
  },
  'executive-summary': {
    sourceReportIds: 'Source reports',
    synthesisFocus: 'Synthesis focus',
  },
};

/**
 * Human-readable labels for the BTM cooling-GENERATION MACHINE axis
 * (`CoolingGeneration` — src/types/siteRag.ts) — "how cold is MADE" (the chiller
 * technology). ADDITIVE — a genuinely distinct axis from the rack-side
 * `coolingStrategy` (how heat leaves the servers) and the cold-END
 * `bopHeatRejection` SINK (where rejected heat goes). Labels name the MACHINE so
 * the absorption (thermal) machine reads as the same concept the
 * `absorption-cchp` cold-end choice carries — keeping the two axes consistent.
 */
export const COOLING_GENERATION_LABELS: Record<CoolingGeneration, string> = {
  'absorption-thermal': 'Absorption chiller — thermal / waste-heat driven',
  'vapour-compression-electric': 'Vapour-compression chiller — electric',
  'steam-driven-mechanical': 'Steam-driven mechanical chiller',
  'free-cooling-economiser': 'Free cooling / economiser — ambient',
  none: 'None',
  unknown: 'Unknown',
};

/**
 * Human-readable labels for the BTM cold-END heat-rejection SINK axis
 * (`BopHeatRejection` — src/types/siteRag.ts) — "where rejected heat GOES" (the
 * axis that actually drives water withdrawal). ADDITIVE — distinct from the
 * rack-side `coolingStrategy` and the cold-PRODUCTION `coolingGeneration` MACHINE.
 * Single source of truth: `coolingValidity.ts` and the Coverage-Matrix review
 * (resolveReportSetupFields.ts) both consume this map.
 */
export const HEAT_REJECTION_LABELS: Record<BopHeatRejection, string> = {
  'dry-cooler': 'Dry cooler — no evaporative water in verified dry mode',
  'wet-cooling-tower': 'Wet cooling tower — evaporative (water)',
  hybrid: 'Hybrid — dry + wet',
  'once-through': 'Once-through — river / sea (water)',
  'absorption-cchp': 'Absorption / CCHP cold end (waste-heat machine)',
  'free-cooling': 'Free cooling — economiser (ambient)',
  unknown: 'Unknown / not yet decided',
};

/* ── P1 BTM-feasibility intent fields (field-architecture audit §3.6/§4) ──
 * Human-readable labels for the SIX net-new canonical BTM intent fields
 * (`ProjectIntent.{interconnectionStatus, fuelAccess, waterSource,
 * landControl, offtake, capacityCreditIntent}` — src/types/siteRag.ts).
 * Consumed by the ungated report-gen review (resolveReportSetupFields.ts)
 * and the Project Intent editor (ProjectIntentForm.tsx). ADDITIVE. */
export const INTERCONNECTION_STATUS_LABELS: Record<
  NonNullable<import('../../types/siteRag').ProjectIntent['interconnectionStatus']>['queueStatus'],
  string
> = {
  none: 'No interconnection request yet',
  'pre-application': 'Pre-application / scoping',
  queued: 'In the interconnection queue',
  study: 'Interconnection study underway',
  'executed-LGIA': 'Executed LGIA (generator interconnection agreement signed)',
  'executed-load-service': 'Load-service agreement accepted',
};

export const INTERCONNECTION_STUDY_PHASE_LABELS: Record<
  NonNullable<NonNullable<import('../../types/siteRag').ProjectIntent['interconnectionStatus']>['studyPhase']>,
  string
> = {
  feasibility: 'Feasibility study',
  'system-impact': 'System-impact study',
  facilities: 'Facilities study',
};

export const FUEL_ACCESS_LABELS: Record<
  NonNullable<import('../../types/siteRag').ProjectIntent['fuelAccess']>['supply'],
  string
> = {
  none: 'No gas / fuel access',
  'near-pipeline': 'Near an existing pipeline',
  'lateral-required': 'Lateral / interconnect required',
  'firm-contracted': 'Firm gas transport contracted',
};

export const WATER_SOURCE_LABELS: Record<
  import('../../types/siteRag').WaterSourceKind | 'none',
  string
> = {
  municipal: 'Municipal supply',
  groundwater: 'Groundwater (wells)',
  surface: 'Surface water (river / lake)',
  reuse: 'Reclaimed / reuse water',
  none: 'No water source identified',
};

export const WATER_RIGHTS_STATUS_LABELS: Record<
  NonNullable<NonNullable<import('../../types/siteRag').ProjectIntent['waterSource']>['rightsStatus']>,
  string
> = {
  none: 'No water rights',
  applied: 'Water rights applied for',
  permitted: 'Water rights permitted',
};

export const OFFTAKE_STRUCTURE_LABELS: Record<
  NonNullable<import('../../types/siteRag').ProjectIntent['offtake']>['structure'],
  string
> = {
  merchant: 'Merchant',
  contracted: 'Contracted PPA',
  hybrid: 'Hybrid (contracted + merchant)',
};

export const CAPACITY_CREDIT_TARGET_LABELS: Record<
  NonNullable<import('../../types/siteRag').ProjectIntent['capacityCreditIntent']>['target'],
  string
> = {
  none: 'No firm-capacity accreditation sought',
  partial: 'Partial firm-capacity (ELCC) accreditation',
  firm: 'Full firm-capacity (ELCC) accreditation',
};

/** Mechanical-camelCase fallback for keys missing from the table.
 *  Lets a partial-rollout doc still render rather than blowing up;
 *  the labels.test.ts guard catches missing entries at CI time. */
export function humanizeKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, c => c.toUpperCase());
}

/** Look up a per-type label, falling back to the mechanical
 *  humanizer when a key isn't in the table. */
export function labelFor(reportType: ReportTypeId, key: string): string {
  return PREFERENCE_LABELS[reportType]?.[key] ?? humanizeKey(key);
}
